'use client';

import { observer } from 'mobx-react-lite';
import { OrderItem as OrderItemType } from '@instamenu/context-checkout';
import { Box, Text, Stack, Button, Counter } from '@instamenu/ui-system-next';
import { Trash, Gift } from 'lucide-react';
import { formatCurrency } from '@instamenu/formatters';
import { useCheckoutContext } from '@instamenu/context-checkout';
import { useRouter, useSearchParams } from 'next/navigation';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import { tableCellStyle, tableStyle } from './order-item-list.css';
import { makePath } from '../utils';

export const OrderItemList = observer(() => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const checkout = useCheckoutContext();
  const order = checkout?.order;
  if (!checkout || !order) return null;
  const orderItems = order.orderItems;
  const onDeleteItem = (orderItem: OrderItemType) => order.removeOrderItem(orderItem);
  const onUpdateItemQuantity = (orderItem: OrderItemType, quantity: number) =>
    orderItem.update({ quantity });

  return (
    <Box css={{ padding: '20px 0' }}>
      <Text>Your items ({order.numItems})</Text>
      <Stack style={{ paddingTop: 20 }}>
        <Table>
          <TableBody>
            {orderItems.map((item) => (
              <TableRow key={item.id ?? item.key}>
                <TableCell css={{ width: 80 }}>
                  {item.loyaltyComped ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Gift size={18} color={themeVars.color.brand} />
                    </div>
                  ) : (
                    <Counter
                      max={
                        typeof item.item.count === 'number'
                          ? item.item.count - checkout.numInCart(item.item.id) + item.quantity
                          : undefined
                      }
                      value={item.quantity}
                      onChange={(v) => onUpdateItemQuantity(item, v)}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box
                    css={{
                      cursor:
                        item.loyaltyComped && checkout.loyalty?.isDollarBased
                          ? 'default'
                          : 'pointer',
                    }}
                    onClick={() => {
                      if (item.loyaltyComped && checkout.loyalty?.isDollarBased) return;
                      router.push(makePath({ orderId: order.id, orderItemId: item.id }), {
                        scroll: false,
                      });
                    }}
                  >
                    <Text variant="caption1">{item.item.name}</Text>
                  </Box>
                  <Text variant="caption2" css={{ color: palette.gray400 }}>
                    {[...item.modifiers] // don't sort in place
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(
                        (modifier, index) =>
                          `${modifier.name}${index < item.modifiers.length - 1 ? ', ' : ' '}`,
                      )}
                  </Text>
                  <Text variant="caption2" maxLines={1} wordBreak css={{ color: palette.gray400 }}>
                    {item.instructions}
                  </Text>
                </TableCell>
                <TableCell css={{ width: 70, textAlign: 'right' }}>
                  <Text variant="numeric">{formatCurrency(item.price)}</Text>
                </TableCell>
                <TableCell css={{ width: 36 }}>
                  <Button variant="text" onClick={() => onDeleteItem(item)} css={{ height: 32 }}>
                    <Trash size={18} color={palette.red600} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
      <div>
        <Button
          variant={'secondary'}
          size="xs"
          onClick={() => {
            const locationPath = makePath({
              restaurantId: order.restaurant.id,
              tableCode: order.table ? order.table : undefined,
              menuId: checkout.menuId,
            });
            const submenus = searchParams.get('submenus');
            const locationUrl = submenus
              ? `${locationPath}?${new URLSearchParams({ submenus })}`
              : locationPath;
            router.push(locationUrl);
          }}
        >
          Add more items
        </Button>
      </div>
    </Box>
  );
});

const TableRow = (props: React.HTMLProps<HTMLTableRowElement>) => {
  return <tr>{props.children}</tr>;
};

const Table = (props: React.HTMLProps<HTMLTableElement>) => (
  <table className={tableStyle} {...props}>
    {props.children}
  </table>
);

const TableBody = (props: React.HTMLProps<HTMLTableSectionElement>) => {
  return <tbody>{props.children}</tbody>;
};

type TableCellProps = React.HTMLProps<HTMLTableCellElement> & {
  css?: React.CSSProperties;
};

const TableCell = (props: TableCellProps) => {
  return (
    <td style={props.css} className={tableCellStyle}>
      {props.children}
    </td>
  );
};
